import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import moment from 'moment-timezone';
import { Helmet } from "react-helmet-async";
import { Loader } from '@googlemaps/js-api-loader';
import { apiService } from '../../services/api.service';
import { trackingService } from '../../services/tracking.service';
import { environment } from '../../config/environment';
import AddressAutocomplete from './AddressAutocomplete';
import Counter from './Counter';
import 'react-datepicker/dist/react-datepicker.css';
import './Booking.css';
import './StepTwo.css';
import './StepThree.css';

interface Location {
  stopType: string;
  name?: string;
  street?: string;
  houseNo?: string;
  city?: string;
  postcode?: string;
  country?: string;
  address: string;
  location: [number, number];
}

interface Vehicle {
  vehicleTypeId: string;
  name: string;
  picture: string;
  pax: number;
  bags: number;
  driverProfit?: number;
  driverProfitReturn?: number;
  total: number;
  totalReturn: number;
}

interface Company {
  _id: string;
  name: string;
  types: any[];
}

const Booking: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState(1);
  const [returnTrip, setReturnTrip] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isTerm, setIsTerm] = useState(false);
  
  const [pickup, setPickup] = useState<Location>({
    stopType: 'pickup',
    address: '',
    location: [0, 0]
  });
  
  const [drop, setDrop] = useState<Location>({
    stopType: 'drop',
    address: '',
    location: [0, 0]
  });
  
  const [formData, setFormData] = useState({
    flight: '',
    phoneCode: '+31',
    phone: '',
    name: '',
    email: '',
    note: '',
    pickupTime: roundToNext5Minutes(new Date()),
    returnPickupTime: roundToNext5Minutes(new Date()),
    bags: 0,
    pax: 1,
  });
  function roundToNext5Minutes(date: Date) {
    const newDate = new Date(date);

    const minutes = newDate.getMinutes();
    const remainder = minutes % 5;

    if (remainder !== 0) {
      newDate.setMinutes(minutes + (5 - remainder));
    }

    newDate.setSeconds(0);
    newDate.setMilliseconds(0);

    return newDate;
  }
  const [vehicleList, setVehicleList] = useState<Vehicle[]>([]);
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [vehicleTypeId, setVehicleTypeId] = useState<string | null>(null);
  const [creatorId, setCreatorId] = useState<string>('');
  const [customerId, setCustomerId] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [logo, setLogo] = useState<string>('');
  const [currency, setCurrency] = useState<string>('');
  const [totalDistance, setTotalDistance] = useState<number>(0);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [pickupError, setPickupError] = useState<string>('');
  const [dropError, setDropError] = useState<string>('');
  const [intermediateStops, setIntermediateStops] = useState<Location[]>([]);
  const [stopErrors, setStopErrors] = useState<{ [key: number]: string }>({});
  const [showStopsModal, setShowStopsModal] = useState(false);
  const [showStopsOverview, setShowStopsOverview] = useState(false);
  const [editingStopIndex, setEditingStopIndex] = useState<number | null>(null);
  const [newStopAddress, setNewStopAddress] = useState<string>('');
  const [newStopError, setNewStopError] = useState<string>('');
  const bookingToolDetail = useRef<any>(null);
  const currencyCode = useRef<string>('');
  
  const [selectedLanguage, setSelectedLanguage] = useState<any>({
    value: 'en',
    label: 'label-title.english',
    img: '/assets/images/flags/en.gif',
  });
  
  const languages = [
    { value: 'en', label: 'label-title.english', img: '/assets/images/flags/en.gif' },
    { value: 'nl', label: 'label-title.dutch', img: '/assets/images/flags/nl.gif' },
    { value: 'fr', label: 'label-title.french', img: '/assets/images/flags/fr.gif' },
    { value: 'es', label: 'label-title.spanish', img: '/assets/images/flags/sp.gif' },
  ];
  
  const [autocompleteService, setAutocompleteService] = useState<any>(null);
  const [placesService, setPlacesService] = useState<any>(null);
  
  const coid = searchParams.get('coid') || '';
  const cuid = searchParams.get('cuid') || '';
  const small = searchParams.get('size')?.toLowerCase() === 's';

  useEffect(() => {
    const languageCode = /* localStorage.getItem('languageCode') || */ 'en';
    const lang = languages.find(l => l.value === languageCode) || languages[0];
    setSelectedLanguage(lang);
    apiService.selectedLanguage = lang;
    i18n.changeLanguage(languageCode);
    getSetting();
  }, []);

  const getCurrentDateTime = (currentDate?: Date): Date => {
    const date = currentDate || new Date();
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date;
  };

  const getSetting = async () => {
    try {
      const response = await apiService.getData(
        environment['Booking Tool'].settingDetails,
        {
          address: searchParams.get('address') || '',
          coid: coid,
          cuid: cuid,
        }
      );
      
      if (response.data) {
        apiService.rederScript(response);
        trackingService.init(response, 'booking');
        setCreatorId(response.data.driverId || '');
        setCustomerId(response.data.customerId || '');
        setCompanyName(response.data.companyName || '');
        setLogo(response.data.companyPicture || '');
        currencyCode.current = (response.data.currencyCode || '').toUpperCase();
        
        // Set theme colors
        bookingToolDetail.current = response.data.bookingToolDetail;
        if (response.data.bookingToolDetail) {
          if(response.data.bookingToolDetail.language){
            let language:any = languages.find((lang:any)=> lang.value === response.data.bookingToolDetail.language)
            if(language){
              switchLanguage(language)
            }
          }
          const root = document.documentElement;
          if (response.data.bookingToolDetail.secondaryTextColorCode) {
            root.style.setProperty('--secondary-text-color', response.data.bookingToolDetail.secondaryTextColorCode);
          }
          if (response.data.bookingToolDetail.primaryColorCode) {
            root.style.setProperty('--theme-color-code', response.data.bookingToolDetail.primaryColorCode);
          }
          if (response.data.bookingToolDetail.primaryTextColorCode) {
            root.style.setProperty('--primary-text-color-code', response.data.bookingToolDetail.primaryTextColorCode);
            root.style.setProperty('--bs-heading-color', response.data.bookingToolDetail.primaryTextColorCode);
          }
        }
        
        // Load Google Maps
        if (response.data.bookingToolDetail?.mapKey) {
          await loadGoogleMaps(response.data.bookingToolDetail.mapKey);
        }
        
        if (response.data.favAddress) {
          setPickup({ ...response.data.favAddress.address, stopType: 'pickup' });
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const loadGoogleMaps = async (apiKey: string) => {
    try {
      const loader = new Loader({
        apiKey: apiKey,
        version: 'weekly',
        libraries: ['places'],
      });
      
      await loader.load();
      
      if (window.google && window.google.maps && window.google.maps.places) {
        // Create AutocompleteService for getting predictions
        const autocomplete = new window.google.maps.places.AutocompleteService();
        setAutocompleteService(autocomplete);
        
        // Create PlacesService for getting place details
        const places = new window.google.maps.places.PlacesService(
          document.createElement('div')
        );
        setPlacesService(places);
      }
    } catch (error) {
      console.error('Error loading Google Maps:', error);
    }
  };

  const handlePlaceSelect = (place: any, type: 'pickup' | 'drop' | 'stop', stopIndex?: number) => {
    const street = place.address_components?.find((c: any) => c.types.includes('route'));
    const houseNo = place.address_components?.find((c: any) => c.types.includes('street_number'));
    const city = place.address_components?.find((c: any) => c.types.includes('locality'));
    const postcode = place.address_components?.find((c: any) => c.types.includes('postal_code'));
    const country = place.address_components?.find((c: any) => c.types.includes('country'));
    
    const lat = place.geometry?.location?.lat() || place.geometry?.location?.lat || 0;
    const lng = place.geometry?.location?.lng() || place.geometry?.location?.lng || 0;
    
    const location: Location = {
      stopType: type === 'stop' ? 'stop' : type,
      address: (place.formatted_address && place.name && !place.formatted_address.includes(place.name)) ? place.name + ", " + place.formatted_address : place.formatted_address,
      street: street?.long_name || '',
      houseNo: houseNo?.long_name || '',
      city: city?.long_name || '',
      postcode: postcode?.long_name || '',
      country: country?.long_name || '',
      location: [lng, lat],
    };
    if (type === 'pickup') {
      setPickup(location);
      setPickupError(''); // Clear error when valid location is selected
      // Check if all locations are set after updating pickup
      setTimeout(() => {
        if (drop.location[0] && drop.location[1] && intermediateStops.every(s => s.location[0] && s.location[1])) {
          //searchPrice();
        }
      }, 100);
    } else if (type === 'drop') {
      setDrop(location);
      setDropError(''); // Clear error when valid location is selected
      // Check if all locations are set after updating drop
      setTimeout(() => {
        if (pickup.location[0] && pickup.location[1] && intermediateStops.every(s => s.location[0] && s.location[1])) {
          //searchPrice();
        }
      }, 100);
    } else if (type === 'stop' && stopIndex !== undefined) {
      const updatedStops = [...intermediateStops];
      updatedStops[stopIndex] = location;
      setIntermediateStops(updatedStops);
      // Clear error for this stop
      const newErrors = { ...stopErrors };
      delete newErrors[stopIndex];
      setStopErrors(newErrors);
      // Check if all locations are set after updating stop
      setTimeout(() => {
        if (pickup.location[0] && pickup.location[1] && drop.location[0] && drop.location[1] && updatedStops.every(s => s.location[0] && s.location[1])) {
          //searchPrice();
        }
      }, 100);
    }
  };

  const addIntermediateStop = () => {
    const newStop: Location = {
      stopType: 'stop',
      address: '',
      location: [0, 0]
    };
    setIntermediateStops([...intermediateStops, newStop]);
  };

  const handleSaveStop = (place: any) => {
    if (!place || !place.geometry || !place.geometry.location) {
      setNewStopError(t('label-title.address-location-not-found'));
      return;
    }

    const street = place.address_components?.find((c: any) => c.types.includes('route'));
    const houseNo = place.address_components?.find((c: any) => c.types.includes('street_number'));
    const city = place.address_components?.find((c: any) => c.types.includes('locality'));
    const postcode = place.address_components?.find((c: any) => c.types.includes('postal_code'));
    const country = place.address_components?.find((c: any) => c.types.includes('country'));
    
    const lat = place.geometry?.location?.lat() || place.geometry?.location?.lat || 0;
    const lng = place.geometry?.location?.lng() || place.geometry?.location?.lng || 0;
    
    if (!lat || !lng || lat === 0 || lng === 0) {
      setNewStopError(t('label-title.address-location-not-found'));
      return;
    }
    
    const location: Location = {
      stopType: 'stop',
      address: place.formatted_address || '',
      street: street?.long_name || '',
      houseNo: houseNo?.long_name || '',
      city: city?.long_name || '',
      postcode: postcode?.long_name || '',
      country: country?.long_name || '',
      location: [lng, lat],
    };

    if (editingStopIndex !== null) {
      // Update existing stop
      const updatedStops = [...intermediateStops];
      updatedStops[editingStopIndex] = location;
      setIntermediateStops(updatedStops);
      // Clear error for this stop
      const newErrors = { ...stopErrors };
      delete newErrors[editingStopIndex];
      setStopErrors(newErrors);
    } else {
      // Add new stop
      setIntermediateStops([...intermediateStops, location]);
    }
    
    setShowStopsModal(false);
    setEditingStopIndex(null);
    setNewStopAddress('');
    setNewStopError('');
    
    // Recalculate price if all locations are set
    setTimeout(() => {
      if (pickup.location[0] && pickup.location[1] && drop.location[0] && drop.location[1]) {
        searchPrice();
      }
    }, 100);
  };

  const handleCloseModal = () => {
    setShowStopsModal(false);
    setEditingStopIndex(null);
    setNewStopAddress('');
    setNewStopError('');
  };

  const removeIntermediateStop = (index: number) => {
    const updatedStops = intermediateStops.filter((_, i) => i !== index);
    setIntermediateStops(updatedStops);
    // Remove error for this stop
    const newErrors = { ...stopErrors };
    delete newErrors[index];
    // Reindex remaining errors
    const reindexedErrors: { [key: number]: string } = {};
    Object.keys(newErrors).forEach(key => {
      const oldIndex = parseInt(key);
      if (oldIndex > index) {
        reindexedErrors[oldIndex - 1] = newErrors[oldIndex];
      } else if (oldIndex < index) {
        reindexedErrors[oldIndex] = newErrors[oldIndex];
      }
    });
    setStopErrors(reindexedErrors);
    // Recalculate price if all locations are set
    setTimeout(() => {
      if (pickup.location[0] && pickup.location[1] && drop.location[0] && drop.location[1] && updatedStops.every(s => s.location[0] && s.location[1])) {
        searchPrice();
      }
    }, 100);
  };

  const validateAddresses = (): boolean => {
    let isValid = true;
    const newStopErrors: { [key: number]: string } = {};
    
    // Validate pickup address
    if (!pickup.address || pickup.address.trim() === '') {
      setPickupError(t('label-title.pickup-address-is-required'));
      isValid = false;
    } else if (!pickup.location[0] || !pickup.location[1] || pickup.location[0] === 0 || pickup.location[1] === 0) {
      setPickupError(t('label-title.address-location-not-found'));
      isValid = false;
    } else {
      setPickupError('');
    }
    
    // Validate intermediate stops
    intermediateStops.forEach((stop, index) => {
      if (!stop.address || stop.address.trim() === '') {
        newStopErrors[index] = t('label-title.stop-address-is-required');
        isValid = false;
      } else if (!stop.location[0] || !stop.location[1] || stop.location[0] === 0 || stop.location[1] === 0) {
        newStopErrors[index] = t('label-title.address-location-not-found');
        isValid = false;
      }
    });
    setStopErrors(newStopErrors);
    
    // Validate drop address
    if (!drop.address || drop.address.trim() === '') {
      setDropError(t('label-title.drop-address-is-required'));
      isValid = false;
    } else if (!drop.location[0] || !drop.location[1] || drop.location[0] === 0 || drop.location[1] === 0) {
      setDropError(t('label-title.address-location-not-found'));
      isValid = false;
    } else {
      setDropError('');
    }
    
    return isValid;
  };

  const getAllStops = (): Location[] => {
    return [pickup, ...intermediateStops, drop];
  };

  const searchPrice = async () => {
    setIsCalculatingPrice(true);
    try {
      const allStops = getAllStops();
      if (coid || cuid) {
        try {
          const response = await apiService.postOrderData(
            environment['Booking Tool'].search,
            {
              // pickupTime: new Date(formData.pickupTime).toISOString(),
              pickupTime: moment(formData.pickupTime).format('YYYY-MM-DD HH:mm'),
              returnPickupTime: formData.returnPickupTime?moment(formData.returnPickupTime).format('YYYY-MM-DD HH:mm'):null,
              return:returnTrip,
              stops: allStops,
              coid: coid,
              cuid: cuid,
            }
          );
          
          if (response.success) {
            response.data.vehicleList = response.data.vehicleList.filter((vehicle: Vehicle) => vehicle.pax >= formData.pax && vehicle.bags >= formData.bags);
            setVehicleList(response.data.vehicleList.sort((a: Vehicle, b: Vehicle) => a.total - b.total));
            setCurrency(response.data.currencySign);
            if (response.data.vehicleList.length) {
              trackingService.step('quoted');
              trackingService.event('quote_shown', {
                value: response.data.vehicleList[0].total,
                currency: currencyCode.current || undefined,
              });
            }
            setTotalDistance(response.data.totalDistance);
            setTotalTime(response.data.totalTime);
            /* if (response.data.vehicleList.length && !vehicleTypeId) {
              setVehicleTypeId(response.data.vehicleList[0].vehicleTypeId);
            } */
          }
        } catch (error) {
          console.error('Error searching price:', error);
        }
      } else {
        try {
          const response = await apiService.postOrderData(
            environment['Booking Tool'].searchAll,
            {
              stops: allStops,
            }
          );
          
          if (response.success) {
            setCompanyList(response.data.companyArray);
            setCurrency(response.data.currencySign);
            setTotalDistance(response.data.totalDistance);
            setTotalTime(response.data.totalTime);
            if (response.data.companyArray.length && !creatorId) {
              setCreatorId(response.data.companyArray[0]._id);
              await setCompany();
            } else if (creatorId) {
              await setCompany();
            }
          }
        } catch (error) {
          console.error('Error searching all:', error);
        }
      }
    } finally {
      setIsCalculatingPrice(false);
    }
  };

  const setCompany = async () => {
    const company = companyList.find(c => c._id === creatorId);
    if (company && company.types) {
      // Map company types to vehicle list format
      const vehicles: Vehicle[] = company.types.map((type: any) => ({
        vehicleTypeId: type.vehicleTypeId,
        name: type.name || 'Vehicle',
        picture: type.picture || '',
        pax: type.pax || 4,
        bags: type.bags || 2,
        driverProfit: type.driverProfit,
        driverProfitReturn: type.driverProfitReturn,
        total: type.amount || 0,
        totalReturn: type.amountReturn || 0,
      }));
      setVehicleList(vehicles.sort((a, b) => a.total - b.total));
      /* if (vehicles.length && !vehicleTypeId) {
        setVehicleTypeId(vehicles[0].vehicleTypeId);
      } */
    }
  };

  const onStepper = (newStep: number) => {
    if (newStep === 2) {
      if (!pickup.location[0] || !drop.location[0]) {
        return;
      }
      // Validate intermediate stops
      if (intermediateStops.some(stop => !stop.location[0] || !stop.location[1] || stop.location[0] === 0 || stop.location[1] === 0)) {
        return;
      }
      if (returnTrip && formData.pickupTime >= formData.returnPickupTime) {
        alert('Return time should be greater than pickup time');
        return false;
      }
      // Generate bag and pax lists based on selected vehicle or default
      if (vehicleTypeId) {
        const vehicle = vehicleList.find(v => v.vehicleTypeId === vehicleTypeId);
        if (vehicle) {
          if (formData.bags > vehicle.bags) {
            setFormData({ ...formData, bags: Math.min(formData.bags, vehicle.bags) });
          }
          if (formData.pax > vehicle.pax) {
            setFormData({ ...formData, pax: Math.min(formData.pax, vehicle.pax) });
          }
          
        }
      }
    }
    setStep(newStep);
  };

  const switchLanguage = (lang: any) => {
     console.log(lang)
    setSelectedLanguage(lang);
    apiService.selectedLanguage = lang;
    i18n.changeLanguage(lang.value);
    // localStorage.setItem('languageCode', lang.value);
  };

  const handleInputChange = (field: string, value: any) => {
    if (['name', 'email', 'phone', 'phoneCode', 'flight', 'note'].includes(field)) {
      trackingService.formStarted();
    }
    setFormData({ ...formData, [field]: value });
  };

  const validateForm = (): boolean => {
    if (!formData.name || !formData.email || !formData.phone || !formData.pickupTime) {
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return false;
    }
    if (returnTrip && formData.pickupTime >= formData.returnPickupTime) {
      alert('Return time should be greater than pickup time');
      return false;
    }
    return true;
  };

  const submitPayment = async () => {
    setIsLoading(true);
    setSubmitted(true);
    // document.body.appendChild(bookingToolDetail.current.marketingScript1);
    if (!validateForm() || !isTerm) {
      setIsLoading(false);
      return;
    }
    
    try {
      const vehicle = vehicleList.find(v => v.vehicleTypeId === vehicleTypeId);
      if (!vehicle) {
        setIsLoading(false);
        return;
      }
      
      const reqData: any = {
        origin: 'Booking Tool',
        pickupTime: moment(formData.pickupTime).format('YYYY-MM-DD HH:mm'),
        stops: getAllStops(),
        flight: formData.flight,
        language: apiService.selectedLanguage.value,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        phoneCode: String(formData.phoneCode),
        note: formData.note,
        vehicleTypeId: vehicleTypeId,
        customerId: customerId || '',
        creatorDriver: creatorId,
        pax: formData.pax,
        totalTime: totalTime,
        returnTrip: returnTrip,
        fareType: 1,
        totalDistance: totalDistance,
        bags: formData.bags,
        total: vehicle.total,
        paymentMode: 0,
      };
      
      // Booking attribution (only when enabled for this company); sent again on the return-trip call
      const attribution = trackingService.getAttribution();
      if (attribution) {
        reqData.attribution = attribution;
      }
      trackingService.step('submitted');
      trackingService.event('begin_checkout', {
        value: vehicle.total + (returnTrip && vehicle.totalReturn ? vehicle.totalReturn : 0),
        currency: currencyCode.current || undefined,
        items: [{ item_id: vehicle.vehicleTypeId, item_name: vehicle.name, price: vehicle.total, quantity: 1 }],
      });

      if (vehicle.driverProfit !== undefined && vehicle.driverProfit >= 0) {
        reqData.driverFees = vehicle.driverProfit;
        reqData.creatorProfit = 100 - ((vehicle.driverProfit * 100) / vehicle.total);
        reqData.creatorProfit = Number(reqData.creatorProfit.toFixed(2));
      }
      console.log(reqData)
      const response = await apiService.postOrderData(
        environment['Booking Tool'].create,
        reqData
      );
      
      if (returnTrip) {
        reqData.pickupTime = moment(formData.returnPickupTime).format('YYYY-MM-DD HH:mm');
        reqData.connectedTripId = response.data.trip._id;
        if(vehicle.totalReturn){
          reqData.total = vehicle.totalReturn;
        }
        if(vehicle.driverProfitReturn){
          reqData.driverProfit = vehicle.driverProfitReturn;
        }
        const returnPickup = { ...drop, stopType: 'pickup' };
        const returnDrop = { ...pickup, stopType: 'drop' };
        // Reverse intermediate stops for return trip
        const returnIntermediateStops = intermediateStops.map(stop => ({ ...stop })).reverse();
        reqData.stops = [returnPickup, ...returnIntermediateStops, returnDrop];
        
        const response2 = await apiService.postOrderData(
          environment['Booking Tool'].create,
          reqData
        );
        
        // Store trip IDs for step 3
        const tripIds = {
          tripId: response.data.trip._id,
          tripId2: response2.data.trip._id
        };
        sessionStorage.setItem('bookingTripIds', JSON.stringify(tripIds));
        setStep(3);
        goToSummary(1);
        setIsLoading(false);
      } else {
        // Store trip ID for step 3
        const tripIds = {
          tripId: response.data.trip._id
        };
        sessionStorage.setItem('bookingTripIds', JSON.stringify(tripIds));
        setStep(3);
        goToSummary(1);
        setIsLoading(false);
        
      }
    } catch (error) {
      console.error('Error submitting booking:', error);
      setIsLoading(false);
    }
  };

  const goToSummary = (gtag:any=null) => {
    const tripIds = JSON.parse(sessionStorage.getItem('bookingTripIds') || '{}');
    const url = tripIds.tripId2 
      ? `/summery?tripId=${tripIds.tripId}&tripId2=${tripIds.tripId2}`
      : `/summery?tripId=${tripIds.tripId}`;
    
    // Add query parameters from current URL
    const coidParam = coid ? `&coid=${coid}` : '';
    const cuidParam = cuid ? `&cuid=${cuid}` : '';
    
    const fullUrl = `${url}${coidParam}${cuidParam}`;
    // With attribution enabled, conversion tracking is server-side: open the summary straight away.
    if (trackingService.isEnabled()) {
      window.open(fullUrl, '_blank');
    } else if (gtag && typeof (window as any).gtag_report_conversion === 'function') {
      (window as any).gtag_report_conversion(fullUrl);
      // (window as any).gtag_report_conversion();
    }else{
      window.open(fullUrl, '_blank');
    }
  };

  const bookAnotherTrip = () => {
    // Reset form and go back to step 1
    resetInfo();
    setStep(1);
    sessionStorage.removeItem('bookingTripIds');
  };

  const openTerms = () => {
    window.open('/terms', '_blank');
  };

  const resetInfo = () => {
    setVehicleTypeId(null);
    setFormData({
      flight: '',
      phoneCode: '+31',
      phone: '',
      name: '',
      email: '',
      note: '',
      pickupTime: getCurrentDateTime(),
      returnPickupTime: getCurrentDateTime(),
      bags: 0,
      pax: 1,
    });
    setPickup({
      stopType: 'pickup',
      address: '',
      location: [0, 0]
    });
    setDrop({
      stopType: 'drop',
      address: '',
      location: [0, 0]
    });
    setIntermediateStops([]);
    setStopErrors({});
    setPickupError('');
    setDropError('');
    setShowStopsModal(false);
    setEditingStopIndex(null);
    setNewStopAddress('');
    setNewStopError('');
    setTotalDistance(0);
    setTotalTime(0);
    setReturnTrip(false);
    setIsTerm(false);
    setVehicleList([]);
    setPickupError('');
    setDropError('');
  };

  return (
    <div className="booking-main">
      <div className="booking-contant">
        <div className="">
          <div className="booking-wrapper">
            <div className="booking-header">
              <h1 className="booking-title">{t('heading-title.book-your-taxi')}</h1>
              <div className="language-select">
                <Select
                  value={selectedLanguage}
                  onChange={switchLanguage}
                  options={languages}
                  isSearchable={false}
                  formatOptionLabel={(option: any) => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img src={option.img} alt="" style={{ width: '20px', marginRight: '8px' }} />
                      {t(option.label)}
                    </div>
                  )}
                />
              </div>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); submitPayment(); }}>
              {step === 1 && (
                <div className="booking-form">
                  <div className="row align-items-center">
                    <div className="col-3">
                      <div className="return-trip-checkbox">
                        <input
                          type="checkbox"
                          id="returnTrip"
                          checked={returnTrip}
                          onChange={(e) => setReturnTrip(e.target.checked)}
                          className="form-check-input"
                        />
                        <label htmlFor="returnTrip" className="form-check-label">
                          {t('label-title.return-trip')}
                        </label>
                      </div>
                    </div>
                    <div className="col-9">
                      {/* Intermediate Stops - Compact Display, aligned to the right of return trip */}
                      
                        {/* {<div className="stops-summary d-flex justify-content-end">
                          <div>
                            <label className="form-label">
                              {t('label-title.stops')} ({intermediateStops.length})
                            </label>
                            <div className="stops-chips">
                            {intermediateStops.length !==0 && (<div
                              className="stop-chip"
                              onClick={() => {
                                setShowStopsOverview(true)
                              }}
                            >
                              <span className="stop-chip-number">{intermediateStops.length}</span>
                            </div>)}
                              {intermediateStops.map((stop, index) => (
                                <div
                                  key={index}
                                  className="stop-chip"
                                  onClick={() => {
                                    setEditingStopIndex(index);
                                    setNewStopAddress(stop.address);
                                    setShowStopsModal(true);
                                  }}
                                >
                                  <span className="stop-chip-number">{index + 1}</span>
                                  <span className="stop-chip-address">
                                    {stop.city || stop.address.split(',')[0] || stop.address}
                                  </span>
                                  <button
                                    type="button"
                                    className="stop-chip-remove"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      removeIntermediateStop(index);
                                    }}
                                    title={t('label-title.remove-stop')}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                      <path
                                        d="M18 6L6 18M6 6L18 18"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            {(intermediateStops.length < 3 && <button
                                type="button"
                                className="stop-chip-add"
                                onClick={() => {
                                  setEditingStopIndex(null);
                                  setNewStopAddress('');
                                  setNewStopError('');
                                  setShowStopsModal(true);
                                }}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path
                                    d="M12 5V19M5 12H19"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                                {t('label-title.add-stop')}
                            </button>)}
                            </div>
                          </div>
                        </div>} */}
                    
                    </div>
                  </div>
                  <div className="row mt-2">
                    <div className="col-4">
                      <AddressAutocomplete
                        value={pickup.address}
                        onChange={(value) => {
                          setPickup({ ...pickup, address: value });
                          if (pickupError) setPickupError(''); // Clear error when user starts typing
                        }}
                        onPlaceSelect={(place) => handlePlaceSelect(place, 'pickup')}
                        placeholder={t('label-title.from')}
                        // placeholder={t('label-title.search-address')}
                        // label={t('label-title.from')}
                        autocompleteService={autocompleteService}
                        placesService={placesService}
                        error={pickupError}
                        hasValidLocation={pickup.location[0] !== 0 && pickup.location[1] !== 0}
                      />
                    </div>
                    <div className="col-4 ">
                      <div className="stops-summary d-flex justify-content-center">
                        <div>
                          {/* <label className="form-label">
                            {t('label-title.stops')} ({intermediateStops.length})
                          </label> */}
                          <div className="stops-chips">
                            {intermediateStops.length !== 0 && (<div
                              className="stop-chip"
                              onClick={() => {
                                setShowStopsOverview(true)
                              }}
                            >
                              <span className="stop-chip-number">{intermediateStops.length}</span>
                            </div>)}
                            {(intermediateStops.length < 3 && <button
                              type="button"
                              className="stop-chip-add"
                              onClick={() => {
                                setEditingStopIndex(null);
                                setNewStopAddress('');
                                setNewStopError('');
                                setShowStopsModal(true);
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path
                                  d="M12 5V19M5 12H19"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              {t('label-title.add-stop')}
                            </button>)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-4 ">
                      <AddressAutocomplete
                        value={drop.address}
                        onChange={(value) => {
                          setDrop({ ...drop, address: value });
                          if (dropError) setDropError(''); // Clear error when user starts typing
                        }}
                        onPlaceSelect={(place) => handlePlaceSelect(place, 'drop')}
                        placeholder={t('label-title.to')}
                        // placeholder={t('label-title.search-address')}
                        // label={t('label-title.to')}
                        autocompleteService={autocompleteService}
                        placesService={placesService}
                        error={dropError}
                        hasValidLocation={drop.location[0] !== 0 && drop.location[1] !== 0}
                      />
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-3">
                      <label className="form-label">{t('label-title.pickup-date')}</label>
                      <div className="date-input-wrapper">
                        <DatePicker
                          selected={formData.pickupTime}
                          onChange={(date: Date) => handleInputChange('pickupTime', date)}
                          dateFormat="dd-MM-yyyy"
                          className="form-control date-input"
                          placeholderText={t('label-title.choose-date')}
                          minDate={new Date()}
                        />
                        <svg className="calendar-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke="#9D9D9D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                    </div>
                    <div className="col-3">
                      <label className="form-label">{t('label-title.pickup-time')}</label>
                      <div className="time-input-wrapper">
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {/* Hour */}
                          <select
                            value={formData.pickupTime?.getHours()}
                            onChange={(e: any) => {
                              const newDate = new Date(formData.pickupTime);
                              newDate.setHours(Number(e.target.value));
                              handleInputChange("pickupTime", newDate);
                            }}
                            className="form-select"
                          >
                            {Array.from({ length: 24 }, (_, i) => (
                              <option key={i} value={i}>
                                {String(i).padStart(2, "0")}
                              </option>
                            ))}
                          </select>

                          :

                          {/* Minute */}
                          <select
                            value={formData.pickupTime?.getMinutes()}
                            onChange={(e: any) => {
                              const newDate = new Date(formData.pickupTime);
                              newDate.setMinutes(Number(e.target.value));
                              handleInputChange("pickupTime", newDate);
                            }}
                            className="form-select"
                          >
                            {Array.from({ length: 60 / 5 }, (_, i) => (
                              <option key={i * 5} value={i * 5}>
                                {String(i * 5).padStart(2, "0")}
                              </option>
                            ))}
                          </select>
                        </div>
                        {/* <DatePicker
                          selected={formData.pickupTime}
                          onChange={(date: Date) => handleInputChange('pickupTime', date)}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={15}
                          dateFormat="HH:mm"
                          timeFormat="HH:mm"
                          className="form-control time-input"
                        /> */}
                      </div>
                    </div>
                    {returnTrip && (
                      <>
                        <div className="col-3">
                          <label className="form-label">{t('label-title.return-date')}</label>
                          <div className="date-input-wrapper">
                            <DatePicker
                              selected={formData.returnPickupTime}
                              onChange={(date: Date) => handleInputChange('returnPickupTime', date)}
                              dateFormat="dd-MM-yyyy"
                              className="form-control date-input"
                              placeholderText={t('label-title.choose-date')}
                              minDate={formData.pickupTime}
                            />
                            <svg className="calendar-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke="#9D9D9D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                        </div>
                        <div className="col-3">
                          <label className="form-label">{t('label-title.return-time')}</label>
                          <div className="time-input-wrapper">
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              {/* Hour */}
                              <select
                                value={formData.returnPickupTime?.getHours()}
                                onChange={(e: any) => {
                                  const newDate = new Date(formData.returnPickupTime);
                                  newDate.setHours(Number(e.target.value));
                                  handleInputChange("returnPickupTime", newDate);
                                }}
                                className="form-select"
                              >
                                {Array.from({ length: 24 }, (_, i) => (
                                  <option key={i} value={i}>
                                    {String(i).padStart(2, "0")}
                                  </option>
                                ))}
                              </select>

                              :

                              {/* Minute */}
                              <select
                                value={formData.returnPickupTime?.getMinutes()}
                                onChange={(e: any) => {
                                  const newDate = new Date(formData.returnPickupTime);
                                  newDate.setMinutes(Number(e.target.value));
                                  handleInputChange("returnPickupTime", newDate);
                                }}
                                className="form-select"
                              >
                                {Array.from({ length: 60 / 5 }, (_, i) => (
                                  <option key={i * 5} value={i * 5}>
                                    {String(i * 5).padStart(2, "0")}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                        </>
                    )}
                  </div>
                  
                  
                  
                  <div className="row mb-4">
                    <div className="col-3 mb-3">
                      <Counter
                        label={t('label-title.passengers')}
                        value={formData.pax}
                        min={1}
                        max={8}
                        onIncrement={() => handleInputChange('pax', formData.pax + 1)}
                        onDecrement={() => handleInputChange('pax', Math.max(1, formData.pax - 1))}
                      />
                    </div>
                    <div className="col-3 mb-3">
                      <Counter
                        label={t('label-title.bags')}
                        value={formData.bags}
                        min={0}
                        max={20}
                        onIncrement={() => handleInputChange('bags', formData.bags + 1)}
                        onDecrement={() => handleInputChange('bags', Math.max(0, formData.bags - 1))}
                      />
                    </div>
                    <div className="col-6 mb-3  mt-4">
                      <div className="text-end">
                        <button
                          type="button"
                          className="btn-calculate-price"
                          onClick={async () => {
                            if (validateAddresses()) {
                              await searchPrice();
                              onStepper(2);
                            }
                          }}
                          disabled={isCalculatingPrice}
                        >
                          {isCalculatingPrice ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: '8px' }}></span>
                              {t('label-title.calculating') || 'Calculating...'}
                            </>
                          ) : (
                            <>
                              {t('label-title.calculate-price')}
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '8px' }}>
                                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                    
                    {/* {!coid && !cuid && companyList.length > 0 && (
                      <div className="mb-2">
                        <h4 className="mb-0">{t('heading-title.select-company')}</h4>
                        <Select
                          value={companyList.find(c => c._id === creatorId)}
                          onChange={(option: any) => {
                            setCreatorId(option._id);
                            setCompany();
                          }}
                          options={companyList}
                          getOptionLabel={(option: Company) => option.name}
                          getOptionValue={(option: Company) => option._id}
                        />
                      </div>
                    )}
                    
                    {vehicleList.length > 0 && (
                      <div className="mb-2">
                        <h4 className="mb-0">{t('heading-title.select-car-type')}</h4>
                        <div className="cars-list">
                          {vehicleList.map((vehicle) => (
                            <div
                              key={vehicle.vehicleTypeId}
                              className={`cars ${vehicleTypeId === vehicle.vehicleTypeId ? 'active' : ''}`}
                              onClick={() => setVehicleTypeId(vehicle.vehicleTypeId)}
                            >
                              <img
                                src={vehicle.picture ? `${environment.FILE_URL}${vehicle.picture}` : '/assets/images/default-car.png'}
                                alt={vehicle.name}
                              />
                              <div className="d-flex justify-content-between align-items-center">
                                <p className="mb-0">{vehicle.name}</p>
                                <span>{vehicle.pax} 👤</span>
                              </div>
                              <div className="d-flex justify-content-between align-items-center">
                                <p className="mb-0">
                                  {currency} {returnTrip ? apiService.roundFun(vehicle.total * 2) : vehicle.total}
                                </p>
                                <span>{vehicle.bags} 🧳</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )} */}
                    
                  
                </div>
              )}
                
                {step === 2 && (
                  <div className="step-two-container">
                    <div className="row">
                      {/* Left Panel */}
                      <div className="col-sm-5 step-two-left order-2 order-sm-1">
                        {/* Ride Information Section */}
                        <div className="info-section">
                          <div className="info-section-header">
                            <h3 className="info-section-title">{t('label-title.ride-information')}</h3>
                            <div className="d-flex align-items-center">
                              {intermediateStops.length > 0 && (
                                <button
                                  type="button"
                                  className="edit-btn me-3"
                                  onClick={() => setShowStopsOverview(true)}
                                >
                                  {t('label-title.view-stops') || 'View stops'}
                                </button>
                              )}
                              <button type="button" className="edit-btn" onClick={() => onStepper(1)}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                  <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M18.5 2.50023C18.8978 2.10243 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10243 21.5 2.50023C21.8978 2.89804 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.10243 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                          
                          {/* Departure */}
                          <div className="location-info departure">
                            <div className="location-icon departure-icon">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" fill="var(--theme-color-code, #007bff)"/>
                                <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" fill="white"/>
                              </svg>
                            </div>
                            <div className="location-details">
                              <div className="location-date-time">
                                {moment(formData.pickupTime).format('DD-MM-YYYY HH:mm')}
                              </div>
                              <div className="location-name">{pickup.city || pickup.address.split(',')[0]}</div>
                              <div className="location-address">{pickup.address}</div>
                            </div>
                          </div>
                          
                          {/* Intermediate Stops */}
                          {/* {intermediateStops.map((stop, index) => (
                            <React.Fragment key={index}>
                              <div className="connection-line"></div>
                              <div className="location-info stop">
                                <div className="location-icon stop-icon">
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" fill="var(--theme-color-code, #007bff)"/>
                                    <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" fill="white"/>
                                  </svg>
                                </div>
                                <div className="location-details">
                                  <div className="location-date-time">
                                    {t('label-title.stop')} {index + 1}
                                  </div>
                                  <div className="location-name">{stop.city || stop.address.split(',')[0]}</div>
                                  <div className="location-address">{stop.address}</div>
                                </div>
                              </div>
                            </React.Fragment>
                          ))} */}
                          
                          {/* Connection Line */}
                          <div className="connection-line"></div>
                          
                          {/* Arrival */}
                          <div className="location-info arrival">
                            <div className="location-icon arrival-icon">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" fill="var(--theme-color-code, #007bff)"/>
                                <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" fill="white"/>
                              </svg>
                            </div>
                            <div className="location-details">
                              <div className="location-date-time">
                                {returnTrip 
                                  ? moment(formData.returnPickupTime).format('DD-MM-YYYY HH:mm')
                                  : moment(formData.pickupTime).add(totalTime, 'minutes').format('DD-MM-YYYY HH:mm')
                                }
                              </div>
                              <div className="location-name">{drop.city || drop.address.split(',')[0]}</div>
                              <div className="location-address">{drop.address}</div>
                            </div>
                          </div>
                          
                          {/* Passengers */}
                          <div className="passengers-info">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>{formData.pax} {t('label-title.persons')}</span>
                          </div>
                          <div className="bags-info">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <path d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M3 6H21" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M16 10C16 11.1046 15.1046 12 14 12C12.8954 12 12 11.1046 12 10" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>{formData.bags} {formData.bags === 1 ? (t('label-title.bag') || 'Bag') : (t('label-title.bags') || 'Bags')}</span>
                          </div>
                        
                        {/* Vehicle Section */}
                        <div className="border-top">
                          <div className="info-section-header">
                            <div className="d-flex align-items-center">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                                <path d="M5 17H19L17 19H7L5 17Z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M19 17V11C19 10.4696 18.7893 9.96086 18.4142 9.58579C18.0391 9.21071 17.5304 9 17 9H7C6.46957 9 5.96086 9.21071 5.58579 9.58579C5.21071 9.96086 5 10.4696 5 11V17" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M7 9V6C7 5.46957 7.21071 4.96086 7.58579 4.58579C7.96086 4.21071 8.46957 4 9 4H15C15.5304 4 16.0391 4.21071 16.4142 4.58579C16.7893 4.96086 17 5.46957 17 6V9" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              
                              <h3 className="info-section-title">{t('label-title.vehicle')}</h3>
                            </div>
                            <button type="button" className="edit-btn" onClick={() => setVehicleTypeId(null)}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M18.5 2.50023C18.8978 2.10243 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10243 21.5 2.50023C21.8978 2.89804 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.10243 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                          <div className="vehicle-selection-status">
                            {vehicleTypeId ? (
                              <div className="selected-vehicle-info">
                                {vehicleList.find(v => v.vehicleTypeId === vehicleTypeId)?.name || t('label-title.select-vehicle-type')}
                              </div>
                            ) : (
                              <div className="no-selection">{t('label-title.select-vehicle-type')}</div>
                            )}
                          </div>
                        </div>
                        
                        {/* Personal Information Section */}
                          {/* <div className="border-top info-section-header">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <h3 className="info-section-title">{t('label-title.personal-information')}</h3>
                          </div>
                          <div className="personal-info-content">
                            {formData.name || formData.email ? (
                              <div className="personal-info-filled">
                                <div><strong>{formData.name}</strong></div>
                                <div>{formData.email}</div>
                                <div>{formData.phoneCode} {formData.phone}</div>
                              </div>
                            ) : (
                              <div className="no-personal-info">{t('label-title.no-personal-info')}</div>
                            )}
                          </div> */}
                        </div>
                      </div>
                      
                      {/* Right Panel - Vehicle Selection */}
                      {!vehicleTypeId && (
                        <div className="col-sm-7 step-two-right">
                          {vehicleList.length > 0 ? (
                            <>
                            <div className="vehicle-selection-grid order-1 order-sm-2">
                              {vehicleList.map((vehicle) => (
                                <div
                                  key={vehicle.vehicleTypeId}
                                  className={`vehicle-card ${vehicleTypeId === vehicle.vehicleTypeId ? 'selected' : ''}`}
                                  onClick={() => setVehicleTypeId(vehicle.vehicleTypeId)}
                                >
                                  <div className="vehicle-image-wrapper">
                                    <img
                                      src={vehicle.picture ? `${environment.FILE_URL}${vehicle.picture}` : '/assets/images/default-car.png'}
                                      alt={vehicle.name}
                                      className="vehicle-image"
                                    />
                                  </div>
                                  <div className="d-flex vehicle-card-row">
                                    <div className="vehicle-type">
                                      <span className="vehicle-type-name">{vehicle.name}</span>
                                      <span className="vehicle-type-price">{currency}{returnTrip ? apiService.roundFunReturn(vehicle.total ,vehicle.totalReturn) : vehicle.total}</span>
                                    </div>
                                    <div className="vehicle-capacity ms-2">
                                      <div className="passengers-info">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                          <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <span>{vehicle.pax}</span>
                                      </div>
                                      <div className="bags-info">
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                          <path d="M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M3 6H21" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                          <path d="M16 10C16 11.1046 15.1046 12 14 12C12.8954 12 12 11.1046 12 10" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        <span>{vehicle.bags}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="step-two-actions">
                              <button
                                type="button"
                                className="btn-back"
                                onClick={() => onStepper(1)}
                              >
                                {t('button-title.back')}
                              </button>
                            </div>
                          </>
                          ) : (
                            <div className="no-vehicles">
                              <p>{t('heading-title.select-car-type')}</p>
                            </div>
                          )}
                        </div>
                      )}
                      {vehicleTypeId && (<div className="col-sm-7 step-two-right  order-1 order-sm-2">
                        <div className="personal-info-form-section">
                          <h4 className="form-section-title">{t('label-title.personal-information')}</h4>
                          <div className="row mb-1">
                            

                            <div className="col-6">
                              <div className="form-group mb-2">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={formData.name}
                                  onChange={(e) => handleInputChange('name', e.target.value)}
                                  placeholder={t('label-title.enter-name')}
                                />
                                {submitted && !formData.name && (
                                  <div className="text-danger">
                                    <small>{t('label-title.name-is-required')}</small>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="col-6">
                              <div className="form-group mb-2">
                                <input
                                  type="email"
                                  className="form-control"
                                  value={formData.email}
                                  onChange={(e) => handleInputChange('email', e.target.value)}
                                  placeholder={t('label-title.enter-email')}
                                />
                                {submitted && !formData.email && (
                                  <div className="text-danger">
                                    <small>{t('label-title.email-is-required')}</small>
                                  </div>
                                )}
                                {submitted && formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (
                                  <div className="text-danger">
                                    <small>{t('label-title.email-is-invalid')}</small>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                              <div className="form-group mb-2">
                                <div style={{ display: 'flex', gap: '5px' }}>
                                  <Select
                                    value={{ value: formData.phoneCode, label: formData.phoneCode }}
                                    onChange={(option: any) => handleInputChange('phoneCode', option.value)}
                                    options={apiService.phoneCodeList.map(code => ({ value: code, label: code }))}
                                    isSearchable={false}
                                    styles={{ container: (base) => ({ ...base, width: '100px' }) }}
                                  />
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    placeholder={t('label-title.enter-phone')}
                                    style={{ flex: 1 }}
                                  />
                                </div>
                                {submitted && !formData.phone && (
                                  <div className="text-danger">
                                    <small>{t('label-title.phone-is-required')}</small>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="col-12 col-sm-6 col-md-4 col-lg-3">
                              <div className="form-group mb-2">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={formData.flight}
                                  onChange={(e) => handleInputChange('flight', e.target.value)}
                                  placeholder={t('label-title.enter-flight-no')}
                                />
                              </div>
                            </div>

                            <div className="col-12 col-md-4 col-lg-6">
                              <div className="form-group mb-2">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={formData.note}
                                  onChange={(e) => handleInputChange('note', e.target.value)}
                                  placeholder={t('label-title.comment')}
                                />
                              </div>
                            </div>

                            <div className="col-12">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={isTerm}
                                  onChange={(e) => setIsTerm(e.target.checked)}
                                  id="termsCheck"
                                />
                                <label className="form-check-label terms" htmlFor="termsCheck">
                                  {t('label-title.accept')}{' '}
                                  <a href="javascript:void(0)" onClick={openTerms}>
                                    {t('label-title.terms-and-conditions')}
                                  </a>{' '}
                                  {t('label-title.and')}{' '}
                                  <a href="javascript:void(0)" onClick={openTerms}>
                                    {t('label-title.privacy-policy')}
                                  </a>
                                </label>
                              </div>
                            </div>
                          </div>

                          <div className="step-two-actions">
                            <button
                              type="button"
                              className="btn-back"
                              onClick={() => setVehicleTypeId(null)}
                            >
                              {t('button-title.back')}
                            </button>
                            <button
                              type="submit"
                              className="btn-checkout"
                              disabled={!isTerm || !vehicleTypeId || isLoading}
                            >
                              {t('button-title.checkout')}
                              {isLoading && <span className="loading">⏳</span>}
                            </button>
                          </div>
                        </div>
                      </div>)}
                    </div>
                    
                    {/* Personal Information Form */}
                    
                  </div>
                )}

                {step === 3 && (
                  <div className="step-three-container">
                    <div className="success-message">
                      <div className="success-icon">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                          <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <h2 className="success-title">{t('label-title.booking-successful')}</h2>
                      <p className="success-description">{t('label-title.booking-success-message')}</p>
                    </div>

                    <div className="step-three-actions">
                      <button
                        type="button"
                        className="btn-book-another"
                        onClick={bookAnotherTrip}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {t('button-title.book-another-trip')}
                      </button>
                      <button
                        type="button"
                        className="btn-view-summary"
                        onClick={goToSummary}
                      >
                      {t('label-title.view-summary')}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '8px' }}>
                          <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  
                  </div>
                )}
              </form>
            <div className="booking-footer">
              <p className="powered-by">Powered by {apiService.powerBy}™</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stops Modal */}
      {showStopsModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content stops-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingStopIndex !== null 
                  ? `${t('label-title.edit-stop')} ${editingStopIndex + 1}`
                  : t('label-title.add-stop')
                }
              </h3>
              <button
                type="button"
                className="modal-close"
                onClick={handleCloseModal}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <AddressAutocomplete
                value={newStopAddress}
                onChange={(value) => {
                  setNewStopAddress(value);
                  if (newStopError) setNewStopError('');
                }}
                onPlaceSelect={(place) => handleSaveStop(place)}
                placeholder={t('label-title.search-address')}
                label={t('label-title.stop-address')}
                autocompleteService={autocompleteService}
                placesService={placesService}
                error={newStopError}
                hasValidLocation={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Stops Overview Modal */}
      {showStopsOverview && (
        <div className="modal-overlay" onClick={() => setShowStopsOverview(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {t('label-title.stops-overview') || t('label-title.stops') || 'Stops'}
              </h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowStopsOverview(false)}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div className="modal-body overflow-auto">
              <div className="location-info departure">
                <div className="location-icon departure-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z"
                      fill="var(--theme-color-code, #007bff)"
                    />
                    <path
                      d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                      fill="white"
                    />
                  </svg>
                </div>
                <div className="location-details">
                  <div className="location-date-time">
                    {moment(formData.pickupTime).format('DD-MM-YYYY HH:mm')}
                  </div>
                  <div className="location-name">
                    {pickup.city || pickup.address.split(',')[0]}
                  </div>
                  <div className="location-address">{pickup.address}</div>
                </div>
              </div>

              {intermediateStops.map((stop, index) => (
                <React.Fragment key={index}>
                  <div className="connection-line"></div>
                  <div className="location-info stop">
                    <div className="location-icon stop-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z"
                          fill="var(--theme-color-code, #007bff)"
                        />
                        <path
                          d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    <div className="location-details">
                      <div className="location-date-time">
                        {t('label-title.stop')} {index + 1} {step === 1 && (<svg onClick={
                          ()=>{
                            intermediateStops.splice(index,1);
                            setIntermediateStops([...intermediateStops]);
                          }
                        } width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>)}
                      </div>
                      <div className="location-name">
                        {stop.city || stop.address.split(',')[0]}
                      </div>
                      <div className="location-address">{stop.address}</div>
                    </div>
                  </div>
                </React.Fragment>
              ))}

              <div className="connection-line"></div>

              <div className="location-info arrival">
                <div className="location-icon arrival-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z"
                      fill="var(--theme-color-code, #007bff)"
                    />
                    <path
                      d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                      fill="white"
                    />
                  </svg>
                </div>
                <div className="location-details">
                  <div className="location-date-time">
                    {returnTrip
                      ? moment(formData.returnPickupTime).format('DD-MM-YYYY HH:mm')
                      : moment(formData.pickupTime)
                          .add(totalTime, 'minutes')
                          .format('DD-MM-YYYY HH:mm')}
                  </div>
                  <div className="location-name">
                    {drop.city || drop.address.split(',')[0]}
                  </div>
                  <div className="location-address">{drop.address}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Booking;

